import React, { useContext, useEffect, useRef } from "react"
import { IAnalysisFolder } from "./IAnalysisFolder"
import { formatFileName } from "../../../../utils/dateFormats";
import { AppContext } from "../../../../appContext";

interface IFullAnalysisCrumbItemProps{
    crumb: IAnalysisFolder, 
    onCrumb: (crumb: IAnalysisFolder)=>void,
    boundsRef?: React.RefObject<Record<string, HTMLDivElement>>,
    isDrop?: boolean,
    dropMessage?: string
}

export const FullAnalysisCrumbItem = ({boundsRef, crumb, onCrumb, isDrop, dropMessage}: IFullAnalysisCrumbItemProps)=>{
    const ref = useRef<HTMLDivElement>(null);
    const {translate} = useContext(AppContext);

    useEffect(()=>{
        if (boundsRef){
            boundsRef.current[crumb.id] = ref.current;
        }
    }, [ref.current]);

    // Check if the crumb name matches a translation key, otherwise use formatFileName
    const getCrumbText = () => {
        // Handle the "Desktop" root crumb specifically
        if (crumb.name === 'Desktop' || crumb.name.toLowerCase() === 'desktop') {
            return translate('desktop_title');
        }
        
        // Handle the "Analysis" root crumb specifically
        if (crumb.name === 'Analysis' || crumb.name.toLowerCase() === 'analysis') {
            return translate('bottomMenu_Analysis');
        }

        // Check for other common analysis folder names that might need translation
        const translationKeys = [
            'full_analysis_all_files',
            'full_analysis_one_list'
        ];

        const matchingKey = translationKeys.find(key => 
            translate(key).toLowerCase() === crumb.name.toLowerCase() || 
            key === crumb.name
        );

        if (matchingKey) {
            return translate(matchingKey);
        }

        return formatFileName(crumb.name);
    };

    return <div ref={ref} className={`button FullAnalysis_crumb ${isDrop && !dropMessage ? 'FullAnalysis_crumb--drop' : ''} ${isDrop && dropMessage ? 'FullAnalysis_crumb--dropError' : ''}`} onClick={()=>onCrumb(crumb)}>
        {getCrumbText()}
    </div>
}

interface IFullAnalysisCrumbsProps{
    dropData?: {id: string, canDrop: string}
    crumbs: Array<IAnalysisFolder>, 
    onCrumb: (crumb: IAnalysisFolder)=>void,
    boundsRef?: React.RefObject<Record<string, HTMLDivElement>>
}

export const FullAnalysisCrumbs = ({boundsRef, crumbs, onCrumb, dropData} : IFullAnalysisCrumbsProps)=>{
    useEffect(()=>{
        if (!boundsRef){
            return;
        }
        const filtered: Record<string, HTMLDivElement> = {};
        crumbs.forEach(it=>{
            filtered[it.id] = boundsRef.current[it.id];
        });
        Object.keys(boundsRef.current).forEach(it=>{
            if (!filtered[it]){
                delete boundsRef.current[it];
            }
        });
    }, [boundsRef?.current, crumbs]);

    return <div className="FullAnalysis_breadcrumbs">
        {crumbs.map(crumb => <FullAnalysisCrumbItem key={crumb.id || crumb.name} boundsRef={boundsRef} dropMessage={dropData?.canDrop} isDrop={crumb.id == dropData?.id} crumb={crumb} onCrumb={onCrumb}></FullAnalysisCrumbItem>)}
    </div>
}